/**
 * Retour du navigateur pendant un exercice.
 *
 * Avant, le bouton « retour » du navigateur (en bas à gauche sur iPhone)
 * quittait l'exercice d'un coup, même quand la feuille de brouillon était
 * ouverte : l'élève croyait revenir à l'écran précédent (la question).
 * Désormais :
 *  - une fenêtre ouverte (feuille de brouillon, cours, calculatrice,
 *    signalement) se FERME, l'élève reste sur l'exercice ;
 *  - sinon, un exercice en cours demande confirmation (« Quitter l'exercice ? »).
 *
 * Seul le RETOUR est intercepté : un lien de l'exercice (page d'achat, carte…)
 * part sans question.
 */

import { useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';

export type ExitGuardDecision = 'allow' | 'close-overlay' | 'confirm';

/** Pur : que faire d'une navigation qui part de l'exercice. */
export function exitGuardDecision({
  action,
  samePath,
  overlayOpen,
  inProgress,
}: {
  action: string;
  samePath: boolean;
  overlayOpen: boolean;
  inProgress: boolean;
}): ExitGuardDecision {
  if (samePath || (action !== 'BACK' && action !== 'GO')) return 'allow';
  if (overlayOpen) return 'close-overlay';
  return inProgress ? 'confirm' : 'allow';
}

export function useExerciseExitGuard({
  overlayOpen,
  closeOverlay,
  inProgress,
}: {
  overlayOpen: boolean;
  closeOverlay: () => void;
  inProgress: boolean;
}) {
  const state = useRef({ overlayOpen, closeOverlay, inProgress });
  state.current = { overlayOpen, closeOverlay, inProgress };
  /** blocage « silencieux » : la fenêtre s'est fermée, aucune question à poser */
  const silent = useRef(false);

  const blocker = useBlocker({
    withResolver: true,
    // un paiement Stripe recharge la page : jamais de fenêtre « quitter le site ? »
    enableBeforeUnload: false,
    shouldBlockFn: ({ action, current, next }) => {
      const d = exitGuardDecision({
        action,
        samePath: current.pathname === next.pathname,
        overlayOpen: state.current.overlayOpen,
        inProgress: state.current.inProgress,
      });
      if (d === 'close-overlay') {
        silent.current = true;
        state.current.closeOverlay();
        return true;
      }
      return d === 'confirm';
    },
  });

  const { status, reset } = blocker;
  useEffect(() => {
    if (status === 'blocked' && silent.current) {
      silent.current = false;
      reset?.();
    }
  }, [status, reset]);

  return {
    confirming: blocker.status === 'blocked' && !silent.current,
    stay: () => blocker.reset?.(),
    leave: () => blocker.proceed?.(),
  };
}
